import { type PropsWithChildren, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import type { GameV1 } from '@/api/games';
import { getAvailableRecommendedCurrency } from '@/components/GameItem/helpers';
import { getSrc } from '@/helpers/common';
import { DEMO_CURRENCY } from '@/helpers/variables';
import { useGetCommonStore } from '@/store';
import { Spinner } from '@/styles/common';
import { GameError } from '../GameError';
import { GameErrorType } from '../types';

interface GameBlockProps {
  game?: GameV1;
  isDemo: boolean;
  isDemoAvailable: boolean;
  isGeoBlocked?: boolean;
  isMobile: boolean;
}

export const GameBlock = ({
  game,
  isDemo,
  isDemoAvailable,
  isGeoBlocked,
  isMobile,
}: PropsWithChildren<GameBlockProps>) => {
  const { blackColor, mainColor } = useTheme();
  const useCommonStore = useGetCommonStore();

  const { isAuthenticated, user, wallets, pendingUser } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      wallets: state.wallets,
      pendingUser: state.pendingUser,
    }))
  );

  const hasCurrency = useMemo(() => {
    if (user?.currency == null) return false;

    return Object.keys(game?.real || {}).includes(user.currency);
  }, [user, game]);

  const srcIframe = useMemo(() => {
    if (pendingUser || !game) return;
    const baseUrl = `/games/${game.id}`;

    if (isDemo && game.real[DEMO_CURRENCY]?.id) {
      return getSrc(`${baseUrl}/${game.real[DEMO_CURRENCY].id}`);
    }

    if (!isDemo && hasCurrency && user?.currency != null) {
      return getSrc(`${baseUrl}/${game.real[user.currency].id}`);
    }
    if (game.real[DEMO_CURRENCY]?.id) {
      return getSrc(`${baseUrl}/${game.real[DEMO_CURRENCY].id}`);
    }
  }, [pendingUser, game, isDemo, hasCurrency, user?.currency]);

  const availableCurrency = useMemo(
    () => game && getAvailableRecommendedCurrency(game.real, wallets),
    [game, wallets]
  );

  if (pendingUser)
    return (
      <div className="loading">
        <Spinner background={blackColor} center color={mainColor} size={50} />
      </div>
    );

  if (isGeoBlocked) {
    return <GameError error={{ type: GameErrorType.GeoBlocked }} />;
  }

  if (game && !isAuthenticated && !isDemo) {
    return (
      <GameError
        error={{
          type: GameErrorType.NotAuth,
          payload: { game, isDemoAvailable, isMobile },
        }}
        isAutoHeight={isMobile}
      />
    );
  }

  if (isAuthenticated && !hasCurrency && availableCurrency && !isDemo) {
    return (
      <GameError
        error={{
          type: GameErrorType.NotAvailCurrency,
          payload: { availableCurrency },
        }}
      />
    );
  }

  if (!srcIframe) return <GameError error={{ type: GameErrorType.NotFound }} />;

  return (
    <iframe
      allowFullScreen={true}
      frameBorder="0"
      ng-src={srcIframe}
      src={srcIframe}
      title={game?.title}
    />
  );
};
